import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { db } from '../firebase'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { collection, doc, getDoc, getDocs, orderBy, query, where } from 'firebase/firestore'
import { buildCompetencyLedger } from './competencyLedger'

const PERFORMANCE_WINDOW_DAYS = 30
const CLASS_DAYS = new Set([1, 2, 3, 4])

const normalizeText = (value) => String(value || '').trim()

const normalizeCourseKey = (value) => normalizeText(value)
  .toLowerCase()
  .replace(/&/g, 'and')
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()

const normalizeTimestamp = (value) => {
  if (!value) return 0
  if (typeof value?.toMillis === 'function') return value.toMillis()
  if (typeof value?.seconds === 'number') return value.seconds * 1000
  if (typeof value === 'number') return value
  const parsed = new Date(value).getTime()
  return Number.isFinite(parsed) ? parsed : 0
}

const getDateFromValue = (value) => {
  const timestamp = normalizeTimestamp(value)
  return timestamp ? new Date(timestamp) : null
}

const formatDateLabel = (value, includeTime = false) => {
  const timestamp = normalizeTimestamp(value)
  if (!timestamp) return 'To be shared'
  return new Intl.DateTimeFormat('en-KE', {
    dateStyle: 'medium',
    ...(includeTime ? { timeStyle: 'short' } : {}),
  }).format(new Date(timestamp))
}

const toDateKey = (value) => {
  const year = value.getFullYear()
  const month = `${value.getMonth() + 1}`.padStart(2, '0')
  const day = `${value.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

const chunkArray = (items = [], size = 10) => {
  const result = []
  for (let index = 0; index < items.length; index += size) {
    result.push(items.slice(index, index + size))
  }
  return result
}

const normalizeCollection = (value, fallbackId = 'item') => {
  if (!Array.isArray(value)) return []
  return value
    .map((item, index) => {
      if (typeof item === 'string') return { id: `${fallbackId}-${index + 1}`, title: item }
      return { id: item?.id || `${fallbackId}-${index + 1}`, ...item }
    })
    .filter((item) => item.title || item.name || item.note || item.body)
}

const normalizeTextList = (value) => {
  if (!value) return []
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item.trim()
        return normalizeText(item?.text || item?.title || item?.description || item?.body)
      })
      .filter(Boolean)
  }
  if (typeof value === 'string') {
    return value
      .split(/\r?\n|•|;/)
      .map((item) => item.trim())
      .filter(Boolean)
  }
  return []
}

const normalizeDailyPlan = (days = []) => (
  Array.isArray(days)
    ? days.map((day, dayIndex) => {
        if (typeof day === 'string') {
          return {
            id: `day-${dayIndex + 1}`,
            day: `Day ${dayIndex + 1}`,
            focus: day,
          }
        }

        return {
          id: day?.id || `day-${dayIndex + 1}`,
          day: day?.day || `Day ${dayIndex + 1}`,
          focus: day?.focus || day?.topic || day?.activities || day?.description || '',
        }
      })
    : []
)

const normalizeScheme = (value) => {
  if (!value) return []

  if (Array.isArray(value)) {
    return value.map((item, index) => {
      if (typeof item === 'string') {
        return {
          id: `week-${index + 1}`,
          week: String(index + 1),
          topic: item,
          dailyPlan: [],
        }
      }

      return {
        id: item?.id || `week-${index + 1}`,
        week: item?.week || String(index + 1),
        topic: item?.topic || `Week ${index + 1}`,
        dailyPlan: normalizeDailyPlan(item?.dailyPlan || item?.days || item?.weekDays || item?.dailyBreakdown),
      }
    })
  }

  if (typeof value === 'string') {
    return value
      .split(/\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item, index) => ({
        id: `week-${index + 1}`,
        week: String(index + 1),
        topic: item,
        dailyPlan: [],
      }))
  }

  return []
}

const resolveCourseField = (data, keys) => {
  for (const key of keys) {
    if (data?.[key]) return data[key]
  }
  return null
}

const getCourseTitle = (data = {}, fallback = '') => (
  data.mcourseName || data.courseName || data.name || data.title || fallback
)

const buildProgressDocId = (studentId, activeCourseName) => (
  `${studentId}_${String(activeCourseName || '').replace(/\//g, '__')}`
)

const getWeekCompletionId = (week = {}, weekIndex = 0) => (
  week.id || week.topic || `week-${weekIndex + 1}`
)

const getWeekProgressUnits = (week = {}, weekIndex = 0) => {
  const weekId = getWeekCompletionId(week, weekIndex)
  const dailyPlan = Array.isArray(week.dailyPlan) ? week.dailyPlan : []

  if (!dailyPlan.length) {
    return [{
      key: `${weekId}::week`,
      label: week.topic || `Week ${week.week || weekIndex + 1}`,
    }]
  }

  return dailyPlan.map((day, dayIndex) => ({
    key: `${weekId}::${normalizeText(day.id || day.day || `day-${dayIndex + 1}`) || `day-${dayIndex + 1}`}`,
    label: day.focus || day.day || `Day ${dayIndex + 1}`,
  }))
}

const expandCompletedDayItems = (progress = {}, scheme = []) => {
  const daySet = new Set(Array.isArray(progress.completedDayItems) ? progress.completedDayItems : [])
  const completedWeekIds = Array.isArray(progress.completedItems) ? progress.completedItems : []

  scheme.forEach((week, weekIndex) => {
    const weekId = getWeekCompletionId(week, weekIndex)
    if (!completedWeekIds.includes(weekId)) return
    getWeekProgressUnits(week, weekIndex).forEach((unit) => daySet.add(unit.key))
  })

  return daySet
}

const getProgressPercent = (progress = {}, scheme = []) => {
  const units = scheme.flatMap((week, weekIndex) => getWeekProgressUnits(week, weekIndex))
  if (!units.length) return 0
  const completed = expandCompletedDayItems(progress, scheme)
  const completedUnitCount = units.filter((unit) => completed.has(unit.key)).length
  return Math.round((completedUnitCount / units.length) * 100)
}

const getCompletedUnitLabels = (progress = {}, scheme = []) => {
  const completed = expandCompletedDayItems(progress, scheme)
  return scheme.flatMap((week, weekIndex) => (
    getWeekProgressUnits(week, weekIndex)
      .filter((unit) => completed.has(unit.key))
      .map((unit) => unit.label)
  ))
}

const hasSavedCompletedAssessment = (assessment) => Boolean(
  assessment?.status === 'completed' && assessment?.assessment
)

const getAssignmentStatusKey = (submission, assessment = null) => {
  if (hasSavedCompletedAssessment(assessment)) return 'reviewed'
  if (!submission) return 'pending'

  const status = normalizeCourseKey(submission.reviewStatus || submission.status || 'submitted')
  if (status.includes('revision') || status.includes('redo')) return 'revision'
  if (status.includes('review')) return 'reviewed'
  if (status.includes('resubmitted')) return 'resubmitted'
  return 'submitted'
}

const getAssessmentAveragePercent = (assessmentDoc) => {
  const rubric = Array.isArray(assessmentDoc?.assessment?.rubric) ? assessmentDoc.assessment.rubric : []
  const percents = rubric
    .map((item) => {
      const score = Number(item?.score)
      const outOf = Number(item?.outOf) || 5
      if (!Number.isFinite(score) || outOf <= 0) return null
      return Math.max(0, Math.min(100, Math.round((score / outOf) * 100)))
    })
    .filter((value) => value !== null)

  if (!percents.length) return 0
  return Math.round(percents.reduce((sum, value) => sum + value, 0) / percents.length)
}

const addCount = (bucket, value) => {
  const text = normalizeText(value)
  if (!text) return
  const key = normalizeCourseKey(text)
  const previous = bucket.get(key)
  bucket.set(key, {
    text: previous?.text || text,
    count: (previous?.count || 0) + 1,
  })
}

const rankCounts = (bucket, limit = 4) => (
  Array.from(bucket.values())
    .sort((first, second) => second.count - first.count || first.text.localeCompare(second.text))
    .slice(0, limit)
)

const extractStudentCourses = (userData = {}) => {
  const courses = []
  const primary = userData.course || userData.courseName || userData.currentCourse
  if (primary) courses.push(primary)

  const secondary = Array.isArray(userData.secondaryCourses) ? userData.secondaryCourses : []
  secondary.forEach((entry) => {
    if (typeof entry === 'string') {
      courses.push(entry)
      return
    }
    const name = entry?.name || entry?.courseName
    if (name) courses.push(name)
  })

  return Array.from(new Set(courses.map((item) => normalizeText(item)).filter(Boolean)))
}

const getCourseMatchSet = (courseName, courseDoc) => new Set([
  normalizeCourseKey(courseName),
  normalizeCourseKey(getCourseTitle(courseDoc, courseName)),
  normalizeCourseKey(courseDoc?.courseKey),
  normalizeCourseKey(courseDoc?.id),
].filter(Boolean))

function StudentPerformanceComp({ embedded = false, forcedCourseName = '' }) {
  const [courseTabs, setCourseTabs] = useState([])
  const [activeCourse, setActiveCourse] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [attendanceSummary, setAttendanceSummary] = useState(null)
  const [courseMetricsMap, setCourseMetricsMap] = useState({})
  const [fireflySummary, setFireflySummary] = useState(null)
  const [assignmentProfileSummary, setAssignmentProfileSummary] = useState(null)
  const [competencyLedgerMap, setCompetencyLedgerMap] = useState({})
  const [competencyLedgerSummary, setCompetencyLedgerSummary] = useState(null)
  const normalizedForcedCourseName = normalizeCourseKey(forcedCourseName)

  useEffect(() => {
    const auth = getAuth()
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setCourseTabs([])
        setActiveCourse('')
        setAttendanceSummary(null)
        setCourseMetricsMap({})
        setFireflySummary(null)
        setAssignmentProfileSummary(null)
        setCompetencyLedgerMap({})
        setCompetencyLedgerSummary(null)
        setLoadError('')
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setLoadError('')

      try {
        const [userSnapshot, allCoursesSnapshot, assignmentsSnapshot, attendanceSnapshot, fireflyRootDoc, assignmentProfileSnap, fireflyProfileSnap] = await Promise.all([
          getDocs(query(collection(db, 'users'), where('userId', '==', user.uid))),
          getDocs(collection(db, 'courses')),
          getDocs(collection(db, 'courseAssignments')),
          getDocs(query(collection(db, 'AttendaceReport'), where('userId', '==', user.uid))),
          getDoc(doc(db, 'plm', user.uid)),
          getDoc(doc(db, 'studentAssignmentLearningProfiles', user.uid)),
          getDoc(doc(db, 'studentFireflyLearningProfiles', user.uid)),
        ])

        const userDoc = userSnapshot.docs[0]
        const userData = userDoc?.data() || {}
        const courses = extractStudentCourses(userData)
        setCourseTabs(courses)
        setActiveCourse((current) => {
          const forcedMatch = normalizedForcedCourseName
            ? courses.find((course) => normalizeCourseKey(course) === normalizedForcedCourseName)
            : ''

          if (forcedMatch) return forcedMatch
          return current && courses.includes(current) ? current : (courses[0] || '')
        })

        const allCourseDocs = allCoursesSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        const courseDocByName = {}

        courses.forEach((courseName) => {
          const normalizedTarget = normalizeCourseKey(courseName)
          const matched = allCourseDocs.find((courseDoc) => {
            const names = [
              courseDoc.mcourseName,
              courseDoc.courseName,
              courseDoc.name,
              courseDoc.title,
              courseDoc.id,
            ]
            return names.some((name) => normalizeCourseKey(name) === normalizedTarget)
          }) || null

          courseDocByName[courseName] = matched
        })

        const studentAssignments = assignmentsSnapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((assignment) => {
            const assignmentStudentIds = Array.isArray(assignment.studentIds) ? assignment.studentIds.map((item) => String(item)) : []
            const matchesStudent = (
              normalizeCourseKey(assignment.audience) === 'all' ||
              assignmentStudentIds.length === 0 ||
              assignmentStudentIds.includes(user.uid)
            )

            if (!matchesStudent) return false

            return courses.some((courseName) => {
              const courseDoc = courseDocByName[courseName]
              const matchSet = getCourseMatchSet(courseName, courseDoc)
              return (
                assignment.courseId === courseDoc?.id ||
                matchSet.has(normalizeCourseKey(assignment.courseKey || assignment.courseName)) ||
                matchSet.has(normalizeCourseKey(assignment.courseName))
              )
            })
          })

        const submissionsByAssignment = {}
        if (studentAssignments.length) {
          const chunks = chunkArray(studentAssignments.map((assignment) => assignment.id).filter(Boolean), 10)

          await Promise.all(chunks.map(async (assignmentChunk) => {
            const submissionSnapshot = await getDocs(
              query(collection(db, 'assignmentSubmissions'), where('assignmentId', 'in', assignmentChunk))
            )

            submissionSnapshot.forEach((docSnap) => {
              const submission = { id: docSnap.id, ...docSnap.data() }
              const submissionStudentId = submission.studentId || submission.studentID || submission.userId || submission.submittedById
              const submissionEmail = normalizeText(submission.studentEmail || submission.userEmail).toLowerCase()
              if (submissionStudentId !== user.uid && submissionEmail !== normalizeText(user.email).toLowerCase()) return

              const current = submissionsByAssignment[submission.assignmentId]
              const currentTs = normalizeTimestamp(current?.updatedAt || current?.submittedAt || current?.createdAt)
              const nextTs = normalizeTimestamp(submission.updatedAt || submission.submittedAt || submission.createdAt)
              if (!current || nextTs >= currentTs) {
                submissionsByAssignment[submission.assignmentId] = submission
              }
            })
          }))
        }

        const assessmentEntries = await Promise.all(
          Object.values(submissionsByAssignment)
            .filter((submission) => submission?.id)
            .map(async (submission) => {
              const assessmentSnap = await getDoc(doc(db, 'assignmentSubmissionAssessments', submission.id))
              return [submission.id, assessmentSnap.exists() ? { id: assessmentSnap.id, ...assessmentSnap.data() } : null]
            })
        )

        const assessmentsBySubmissionId = Object.fromEntries(assessmentEntries)

        const metricsEntries = await Promise.all(courses.map(async (courseName) => {
          const courseDoc = courseDocByName[courseName]
          const matchSet = getCourseMatchSet(courseName, courseDoc)
          const scheme = normalizeScheme(resolveCourseField(courseDoc, ['schemeOfWork', 'scheme', 'courseScheme', 'schemeTemplate']))
          const projects = normalizeCollection(resolveCourseField(courseDoc, ['projects', 'projectMilestones', 'courseProjects']), 'project')
          const resolvedCourseTitle = getCourseTitle(courseDoc || {}, courseName)

          const progressDocIds = Array.from(new Set([
            buildProgressDocId(user.uid, courseName),
            buildProgressDocId(user.uid, resolvedCourseTitle),
          ]))

          let progressData = { completedItems: [], completedDayItems: [] }
          for (const progressDocId of progressDocIds) {
            const progressSnap = await getDoc(doc(db, 'courseProgress', progressDocId))
            if (progressSnap.exists()) {
              progressData = progressSnap.data() || progressData
              break
            }
          }

          const courseAssignments = studentAssignments.filter((assignment) => (
            assignment.courseId === courseDoc?.id ||
            matchSet.has(normalizeCourseKey(assignment.courseKey || assignment.courseName)) ||
            matchSet.has(normalizeCourseKey(assignment.courseName))
          ))
          const courseAssignmentsWithEvidence = courseAssignments.map((assignment) => {
            const submission = submissionsByAssignment[assignment.id] || null
            return {
              ...assignment,
              submission,
              assessment: submission?.id ? assessmentsBySubmissionId[submission.id] : null,
            }
          })

          const recentReviews = []
          const strengthCounts = new Map()
          const focusCounts = new Map()

          let submittedCount = 0
          let reviewedCount = 0
          let onTimeCount = 0
          let dueAwareSubmissionCount = 0
          let scoreTotal = 0
          let scoredReviewCount = 0

          courseAssignmentsWithEvidence.forEach((assignment) => {
            const submission = assignment.submission
            const assessment = assignment.assessment
            const statusKey = getAssignmentStatusKey(submission, assessment)
            const dueTimestamp = normalizeTimestamp(assignment.dueDate || assignment.dueAt || assignment.deadline || assignment.assignmentDeadline)
            const submissionTimestamp = normalizeTimestamp(submission?.updatedAt || submission?.submittedAt || submission?.createdAt)

            if (submission && statusKey !== 'pending') {
              submittedCount += 1
              if (dueTimestamp && submissionTimestamp) {
                dueAwareSubmissionCount += 1
                if (submissionTimestamp <= dueTimestamp) onTimeCount += 1
              }
            }

            if (statusKey === 'reviewed' && hasSavedCompletedAssessment(assessment)) {
              reviewedCount += 1
              const averagePercent = getAssessmentAveragePercent(assessment)
              if (averagePercent > 0) {
                scoreTotal += averagePercent
                scoredReviewCount += 1
              }

              const reviewPayload = assessment.assessment || {}
              ;(Array.isArray(reviewPayload.strengths) ? reviewPayload.strengths : []).forEach((item) => addCount(strengthCounts, item))
              ;(Array.isArray(reviewPayload.growthAreas) ? reviewPayload.growthAreas : []).forEach((item) => addCount(focusCounts, item))
              ;(Array.isArray(reviewPayload.misconceptions) ? reviewPayload.misconceptions : []).forEach((item) => addCount(focusCounts, item))

              recentReviews.push({
                assignmentTitle: assignment.assignmentTitle || assignment.title || assignment.name || 'Assignment',
                reviewedAtMs: Math.max(
                  Number(assessment.updatedAtMs) || 0,
                  Number(assessment.generatedAtMs) || 0,
                  normalizeTimestamp(assessment.updatedAt),
                  normalizeTimestamp(assessment.generatedAt)
                ),
                headline: normalizeText(reviewPayload.summary?.headline || reviewPayload.summary?.overallSignal || reviewPayload.summary?.overallAssessment),
                averagePercent,
              })
            }
          })

          const progressUnits = scheme.flatMap((week, weekIndex) => getWeekProgressUnits(week, weekIndex))
          const completedUnits = expandCompletedDayItems(progressData, scheme)
          const completedUnitLabels = getCompletedUnitLabels(progressData, scheme)
          const completionPercent = getProgressPercent(progressData, scheme)

          return [courseName, {
            courseDoc,
            scheme,
            progressData,
            assignmentsTotal: courseAssignments.length,
            submittedCount,
            reviewedCount,
            pendingCount: Math.max(courseAssignments.length - submittedCount, 0),
            onTimeCount,
            dueAwareSubmissionCount,
            onTimeRate: dueAwareSubmissionCount ? Math.round((onTimeCount / dueAwareSubmissionCount) * 100) : 0,
            averageScore: scoredReviewCount ? Math.round(scoreTotal / scoredReviewCount) : 0,
            recentReviews: recentReviews
              .sort((first, second) => second.reviewedAtMs - first.reviewedAtMs)
              .slice(0, 5),
            strengthSignals: rankCounts(strengthCounts, 4),
            focusSignals: rankCounts(focusCounts, 4),
            projectsCount: projects.length,
            completionPercent,
            completedUnits: completedUnits.size,
            totalUnits: progressUnits.length,
            completedUnitLabels,
            progressUpdatedAtMs: normalizeTimestamp(progressData.updatedAt || progressData.lastUpdatedAt) || Number(progressData.updatedAtMs) || 0,
            courseAssignments: courseAssignmentsWithEvidence,
          }]
        }))

        const courseMetrics = Object.fromEntries(metricsEntries)
        setCourseMetricsMap(courseMetrics)

        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const windowStart = new Date(today)
        windowStart.setDate(windowStart.getDate() - (PERFORMANCE_WINDOW_DAYS - 1))

        let expectedClassDays = 0
        const cursor = new Date(windowStart)
        while (cursor <= today) {
          if (CLASS_DAYS.has(cursor.getDay())) expectedClassDays += 1
          cursor.setDate(cursor.getDate() + 1)
        }

        const attendedDays = new Set()
        const recentAttendance = attendanceSnapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .map((entry) => {
            const signInDate = getDateFromValue(entry.signInTime || entry.startTime)
            return {
              ...entry,
              signInDate,
              signInTs: signInDate?.getTime() || 0,
            }
          })
          .filter((entry) => entry.signInDate)
          .sort((first, second) => second.signInTs - first.signInTs)

        recentAttendance.forEach((entry) => {
          const normalizedDate = new Date(entry.signInDate)
          normalizedDate.setHours(0, 0, 0, 0)
          if (normalizedDate < windowStart || normalizedDate > today) return
          if (!CLASS_DAYS.has(normalizedDate.getDay())) return
          const status = normalizeText(entry.status).toLowerCase()
          if (status.includes('absent')) return
          attendedDays.add(toDateKey(normalizedDate))
        })

        setAttendanceSummary({
          rate: expectedClassDays ? Math.round((attendedDays.size / expectedClassDays) * 100) : 0,
          attendedDays: attendedDays.size,
          expectedClassDays,
          missedDays: Math.max(expectedClassDays - attendedDays.size, 0),
          recentEntries: recentAttendance.slice(0, 5).map((entry) => ({
            id: entry.id,
            dateLabel: formatDateLabel(entry.signInDate, true),
            status: normalizeText(entry.status) || 'present',
          })),
        })

        const fireflySessionsSnapshot = await getDocs(
          query(collection(db, 'plm', user.uid, 'sessions'), orderBy('endedAtMs', 'desc'))
        )
        const fireflySessions = fireflySessionsSnapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((item) => item.status !== 'started')

        const fireflyProfileData = fireflyProfileSnap.exists() ? fireflyProfileSnap.data() || {} : {}
        const fireflyRecommendation = fireflyProfileData.profile?.fireflyRecommendation || {}

        setFireflySummary({
          sessionCount: fireflySessions.length,
          lastStartedAt: fireflySessions[0]?.startedAtMs || 0,
          lastEndedAt: fireflySessions[0]?.endedAtMs || 0,
          evidenceStrength: normalizeText(fireflyProfileData.profileMeta?.evidenceStrength) || 'live',
          readinessBand: normalizeText(fireflyProfileData.profile?.profileSignals?.readinessBand) || 'building profile',
          headline: normalizeText(fireflyProfileData.profile?.summary?.headline || fireflyRootDoc.data()?.lastSessionSummary),
          studentSummary: normalizeText(fireflyRecommendation.studentSummary || fireflyProfileData.profile?.studentFacing?.encouragement),
          focusConcepts: Array.isArray(fireflyRecommendation.recommendedFocusConcepts)
            ? fireflyRecommendation.recommendedFocusConcepts.map((item) => normalizeText(item?.label || item?.title)).filter(Boolean).slice(0, 4)
            : [],
        })

        const assignmentProfileData = assignmentProfileSnap.exists() ? assignmentProfileSnap.data() || {} : {}
        setAssignmentProfileSummary({
          readinessBand: normalizeText(assignmentProfileData.profile?.profileSignals?.readinessBand),
          evidenceStrength: normalizeText(assignmentProfileData.profileMeta?.evidenceStrength),
          reviewCount: Number(assignmentProfileData.profileMeta?.sourceCount) || 0,
          headline: normalizeText(assignmentProfileData.profile?.summary?.headline),
          learnerSnapshot: normalizeText(assignmentProfileData.profile?.summary?.learnerSnapshot),
        })

        const { ledgerByCourse, summary } = buildCompetencyLedger({
          courseTabs: courses,
          assignmentsByCourse: Object.fromEntries(
            courses.map((courseName) => [courseName, courseMetrics[courseName]?.courseAssignments || []]),
          ),
          progressByCourse: Object.fromEntries(
            courses.map((courseName) => [courseName, {
              scheme: courseMetrics[courseName]?.scheme || [],
              progressData: courseMetrics[courseName]?.progressData || {},
              completedUnitLabels: courseMetrics[courseName]?.completedUnitLabels || [],
              updatedAtMs: courseMetrics[courseName]?.progressUpdatedAtMs || 0,
            }]),
          ),
          fireflySessions,
          assignmentProfileData,
          fireflyProfileData,
        })

        setCompetencyLedgerMap(ledgerByCourse)
        setCompetencyLedgerSummary(summary)
      } catch (error) {
        console.error('Error loading student performance:', error)
        setLoadError('We could not load your performance view right now. Please try again shortly.')
      } finally {
        setIsLoading(false)
      }
    })

    return () => unsubscribe()
  }, [normalizedForcedCourseName])

  useEffect(() => {
    if (!normalizedForcedCourseName || !courseTabs.length) return

    const forcedMatch = courseTabs.find((course) => normalizeCourseKey(course) === normalizedForcedCourseName)
    if (forcedMatch && forcedMatch !== activeCourse) {
      setActiveCourse(forcedMatch)
    }
  }, [activeCourse, courseTabs, normalizedForcedCourseName])

  const activeMetrics = useMemo(() => courseMetricsMap[activeCourse] || null, [activeCourse, courseMetricsMap])
  const activeCompetencies = useMemo(() => competencyLedgerMap[activeCourse] || [], [activeCourse, competencyLedgerMap])

  const summaryCards = useMemo(() => ([
    {
      label: 'Attendance rate',
      value: attendanceSummary ? `${attendanceSummary.rate}%` : '--',
      helper: attendanceSummary
        ? `${attendanceSummary.attendedDays}/${attendanceSummary.expectedClassDays} class days attended in the last 30 days`
        : 'Last 30 days',
      muted: !attendanceSummary,
    },
    {
      label: 'Average review score',
      value: activeMetrics?.reviewedCount ? `${activeMetrics.averageScore}%` : '--',
      helper: activeMetrics?.reviewedCount
        ? `${activeMetrics.reviewedCount} reviewed assignment${activeMetrics.reviewedCount === 1 ? '' : 's'} in ${activeCourse || 'this course'}`
        : 'No reviewed assignments yet for this course',
      muted: !activeMetrics?.reviewedCount,
    },
    {
      label: 'On-time submissions',
      value: activeMetrics?.dueAwareSubmissionCount ? `${activeMetrics.onTimeRate}%` : '--',
      helper: activeMetrics?.dueAwareSubmissionCount
        ? `${activeMetrics.onTimeCount}/${activeMetrics.dueAwareSubmissionCount} submissions arrived on time`
        : 'Will update once due dates and submissions exist',
      muted: !activeMetrics?.dueAwareSubmissionCount,
    },
    {
      label: 'Firefly sessions',
      value: fireflySummary ? String(fireflySummary.sessionCount) : '--',
      helper: fireflySummary?.lastEndedAt
        ? `Last session ended ${formatDateLabel(fireflySummary.lastEndedAt, true)}`
        : 'No Firefly sessions recorded yet',
      muted: !fireflySummary?.sessionCount,
    },
  ]), [activeCourse, activeMetrics, attendanceSummary, fireflySummary])

  const performanceState = useMemo(() => {
    if (!activeMetrics) return null
    return [
      { label: 'Assignments issued', value: activeMetrics.assignmentsTotal },
      { label: 'Submitted', value: activeMetrics.submittedCount },
      { label: 'Reviewed', value: activeMetrics.reviewedCount },
      { label: 'Pending', value: activeMetrics.pendingCount },
    ]
  }, [activeMetrics])

  const competencyStatusBreakdown = useMemo(() => ({
    advanced: activeCompetencies.filter((item) => item.status === 'advanced').length,
    proficient: activeCompetencies.filter((item) => item.status === 'proficient').length,
    developing: activeCompetencies.filter((item) => item.status === 'developing').length,
    emerging: activeCompetencies.filter((item) => item.status === 'emerging').length,
  }), [activeCompetencies])

  const topActiveCompetency = useMemo(() => (
    activeCompetencies.length
      ? [...activeCompetencies].sort((first, second) => (
          second.evidenceCount - first.evidenceCount || second.score - first.score
        ))[0]
      : null
  ), [activeCompetencies])

  return (
    <div className={`performancePage ${embedded ? 'performancePageEmbedded' : ''}`}>
      <div className='performanceHeader'>
        <div>
          <h4>{embedded ? 'Course Performance' : 'Student Performance'}</h4>
          <p>
            {embedded
              ? 'Live performance signals for this course, built from attendance, assignment reviews, course progress, and Firefly learning records.'
              : 'Live performance signals pulled from attendance, assignment reviews, course progress, and Firefly learning records across the platform.'}
          </p>
        </div>
        <div className='performanceBadge'>{activeCourse || 'Course Overview'}</div>
      </div>

      {!embedded && (
        <div className='performanceTabs'>
          {isLoading ? (
            <span className='performanceTab performanceTab--loading'>Loading courses...</span>
          ) : courseTabs.length === 0 ? (
            <span className='performanceTab performanceTab--loading'>No courses found</span>
          ) : (
            courseTabs.map((course) => (
              <button
                key={course}
                type='button'
                className={`performanceTab ${activeCourse === course ? 'active' : ''}`}
                onClick={() => setActiveCourse(course)}
              >
                {course}
              </button>
            ))
          )}
        </div>
      )}

      {loadError ? (
        <div className='performanceEmptyPanel performanceErrorPanel'>
          <p>{loadError}</p>
        </div>
      ) : null}

      <div className='performanceCards'>
        {summaryCards.map((card) => (
          <div className={`performanceCard ${card.muted ? 'performanceCard--muted' : ''}`} key={card.label}>
            <p className='performanceCardLabel'>{card.label}</p>
            <h3 className='performanceCardValue'>{isLoading ? '...' : card.value}</h3>
            <span className='performanceCardHelper'>{card.helper}</span>
          </div>
        ))}
      </div>

      <div className='performanceGrid'>
        <div className='performancePanel'>
          <div className='performancePanelHeader'>
            <h5>Attendance</h5>
            <span className='performanceSub'>Last 30 days</span>
          </div>

          {attendanceSummary ? (
            <>
              <div className='performanceMiniStats'>
                <div className='performanceMiniStat'>
                  <span>Attended</span>
                  <strong>{attendanceSummary.attendedDays}</strong>
                </div>
                <div className='performanceMiniStat'>
                  <span>Missed</span>
                  <strong>{attendanceSummary.missedDays}</strong>
                </div>
                <div className='performanceMiniStat'>
                  <span>Expected</span>
                  <strong>{attendanceSummary.expectedClassDays}</strong>
                </div>
              </div>

              <div className='performanceReviewList'>
                {attendanceSummary.recentEntries.length > 0 ? (
                  attendanceSummary.recentEntries.map((entry) => (
                    <div className='performanceReviewItem' key={entry.id}>
                      <div>
                        <strong>{entry.dateLabel}</strong>
                        <p>Attendance check-in recorded</p>
                      </div>
                      <span className={`statusPill ${normalizeText(entry.status).toLowerCase().includes('late') ? 'statusPill--late' : 'statusPill--present'}`}>
                        {entry.status}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className='performanceEmptyPanel'>
                    <p>No attendance records were found in the last 30 days.</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className='performanceEmptyPanel'>
              <p>Attendance insights will appear here once your check-ins are available.</p>
            </div>
          )}
        </div>

        <div className='performancePanel'>
          <div className='performancePanelHeader'>
            <h5>Assignment Momentum</h5>
            <span className='performanceSub'>{activeCourse || 'Course signals'}</span>
          </div>

          {activeMetrics ? (
            <>
              <div className='performanceMiniStats'>
                {performanceState.map((item) => (
                  <div className='performanceMiniStat' key={item.label}>
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>

              <div className='performanceSignalGroups'>
                <div>
                  <span className='performanceSignalLabel'>Recurring strengths</span>
                  <div className='performanceChipRow'>
                    {activeMetrics.strengthSignals.length > 0 ? (
                      activeMetrics.strengthSignals.map((item) => (
                        <span className='performanceChip' key={`strength-${item.text}`}>{item.text}</span>
                      ))
                    ) : (
                      <span className='performanceChip performanceChipMuted'>Strength patterns will appear after more reviewed assignments.</span>
                    )}
                  </div>
                </div>

                <div>
                  <span className='performanceSignalLabel'>Growth themes</span>
                  <div className='performanceChipRow'>
                    {activeMetrics.focusSignals.length > 0 ? (
                      activeMetrics.focusSignals.map((item) => (
                        <span className='performanceChip performanceChipWarn' key={`focus-${item.text}`}>{item.text}</span>
                      ))
                    ) : (
                      <span className='performanceChip performanceChipMuted'>Growth themes will appear after more reviewed assignments.</span>
                    )}
                  </div>
                </div>
              </div>

              <div className='performanceReviewList'>
                {activeMetrics.recentReviews.length > 0 ? (
                  activeMetrics.recentReviews.map((review) => (
                    <div className='performanceReviewItem' key={`${review.assignmentTitle}-${review.reviewedAtMs}`}>
                      <div>
                        <strong>{review.assignmentTitle}</strong>
                        <p>{review.headline || 'Reviewed assignment feedback is available.'}</p>
                      </div>
                      <span>{review.averagePercent}%</span>
                    </div>
                  ))
                ) : (
                  <div className='performanceEmptyPanel'>
                    <p>Recent reviewed assignments for this course will appear here once grading is available.</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className='performanceEmptyPanel'>
              <p>Select a course to see assignment performance for that course.</p>
            </div>
          )}
        </div>
      </div>

      <div className='performancePanel performancePanelFull'>
        <div className='performancePanelHeader'>
          <h5>Course Progress & Milestones</h5>
          <span className='performanceSub'>{activeCourse || 'Course completion'}</span>
        </div>

        {activeMetrics ? (
          <>
            <div className='performanceProgressCard'>
              <div className='performanceProgressTop'>
                <strong>{activeMetrics.completionPercent}% complete</strong>
                <span>{activeMetrics.completedUnits}/{activeMetrics.totalUnits || 0} learning units tracked</span>
              </div>
              <div className='performanceProgressTrack'>
                <span style={{ width: `${Math.min(Math.max(activeMetrics.completionPercent, 0), 100)}%` }} />
              </div>
            </div>

            <div className='performanceMiniStats performanceMiniStatsWide'>
              <div className='performanceMiniStat'>
                <span>Course milestones</span>
                <strong>{activeMetrics.projectsCount}</strong>
              </div>
              <div className='performanceMiniStat'>
                <span>On-time rate</span>
                <strong>{activeMetrics.dueAwareSubmissionCount ? `${activeMetrics.onTimeRate}%` : '--'}</strong>
              </div>
              <div className='performanceMiniStat'>
                <span>Average reviewed score</span>
                <strong>{activeMetrics.reviewedCount ? `${activeMetrics.averageScore}%` : '--'}</strong>
              </div>
            </div>
          </>
        ) : (
          <div className='performanceEmptyPanel'>
            <p>Course progress will appear here once a course is selected and learning units have been published.</p>
          </div>
        )}
      </div>

      <div className='performancePanel performancePanelFull'>
        <div className='performancePanelHeader'>
          <div>
            <h5>Skills & Competency Ledger</h5>
            <span className='performanceSub'>Curriculum anchors, assignment reviews, tutoring signals, and profile evidence merged into one ledger</span>
          </div>
          {competencyLedgerSummary?.trackedCompetencies ? (
            <span className='performanceSub'>
              {competencyLedgerSummary.trackedCompetencies} competencies tracked
            </span>
          ) : null}
        </div>

        {activeCompetencies.length > 0 ? (
          <>
            <div className='performanceMiniStats performanceMiniStatsWide'>
              <div className='performanceMiniStat'>
                <span>Advanced</span>
                <strong>{competencyStatusBreakdown.advanced}</strong>
              </div>
              <div className='performanceMiniStat'>
                <span>Proficient</span>
                <strong>{competencyStatusBreakdown.proficient}</strong>
              </div>
              <div className='performanceMiniStat'>
                <span>Developing</span>
                <strong>{competencyStatusBreakdown.developing}</strong>
              </div>
              <div className='performanceMiniStat'>
                <span>Emerging</span>
                <strong>{competencyStatusBreakdown.emerging}</strong>
              </div>
            </div>

            {topActiveCompetency ? (
              <div className='performanceLedgerHighlight'>
                <span className='performanceSignalLabel'>Most evidenced right now</span>
                <strong>{topActiveCompetency.label}</strong>
                <p>
                  {topActiveCompetency.evidenceCount} evidence item{topActiveCompetency.evidenceCount === 1 ? '' : 's'} across {topActiveCompetency.sourceCount} source
                  {topActiveCompetency.sourceCount === 1 ? '' : 's'} support this competency.
                </p>
              </div>
            ) : null}

            <div className='performanceLedgerGrid'>
              {activeCompetencies.map((competency) => (
                <article className='performanceLedgerCard' key={competency.id}>
                  <div className='performanceLedgerTop'>
                    <div>
                      <h6>{competency.label}</h6>
                      <p>
                        {competency.courses[0] || activeCourse || 'General learning signal'}
                      </p>
                    </div>
                    <div className='performanceLedgerScoreWrap'>
                      <span className={`performanceLedgerStatus performanceLedgerStatus--${competency.status}`}>
                        {competency.status}
                      </span>
                      <strong>{competency.score}%</strong>
                    </div>
                  </div>

                  <div className='performanceMiniStats performanceLedgerStats'>
                    <div className='performanceMiniStat'>
                      <span>Evidence</span>
                      <strong>{competency.evidenceCount}</strong>
                    </div>
                    <div className='performanceMiniStat'>
                      <span>Sources</span>
                      <strong>{competency.sourceCount}</strong>
                    </div>
                    <div className='performanceMiniStat'>
                      <span>Confidence</span>
                      <strong>{competency.confidence}</strong>
                    </div>
                  </div>

                  {competency.curriculumAnchors.length > 0 ? (
                    <div className='performanceSignalGroups'>
                      <div>
                        <span className='performanceSignalLabel'>Curriculum anchors</span>
                        <div className='performanceChipRow'>
                          {competency.curriculumAnchors.map((item) => (
                            <span className='performanceChip performanceChipAlt' key={`${competency.id}-anchor-${item}`}>{item}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <div className='performanceSignalGroups'>
                    <div>
                      <span className='performanceSignalLabel'>Supporting evidence</span>
                      {competency.supportingSignals.length > 0 ? (
                        <ul className='performanceLedgerList'>
                          {competency.supportingSignals.map((item) => (
                            <li key={`${competency.id}-support-${item}`}>{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className='performanceLedgerEmpty'>Positive evidence will appear here as more reviewed work and sessions accumulate.</p>
                      )}
                    </div>

                    <div>
                      <span className='performanceSignalLabel'>What still needs work</span>
                      {competency.riskSignals.length > 0 ? (
                        <ul className='performanceLedgerList'>
                          {competency.riskSignals.map((item) => (
                            <li key={`${competency.id}-risk-${item}`}>{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className='performanceLedgerEmpty'>No recurring risk signal is standing out yet for this competency.</p>
                      )}
                    </div>
                  </div>

                  <div className='performanceSignalGroups'>
                    <div>
                      <span className='performanceSignalLabel'>Next move</span>
                      {competency.nextMoves.length > 0 ? (
                        <div className='performanceChipRow'>
                          {competency.nextMoves.map((item) => (
                            <span className='performanceChip performanceChipWarn' key={`${competency.id}-move-${item}`}>{item}</span>
                          ))}
                        </div>
                      ) : (
                        <p className='performanceLedgerEmpty'>Next-step coaching will appear once stronger evidence is available.</p>
                      )}
                    </div>

                    <div>
                      <span className='performanceSignalLabel'>Evidence sources</span>
                      <div className='performanceChipRow'>
                        {competency.sources.map((item) => (
                          <span className='performanceChip performanceChipMuted' key={`${competency.id}-source-${item}`}>{item}</span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {competency.lastUpdatedAtMs ? (
                    <div className='performanceLedgerFooter'>
                      Last updated {formatDateLabel(competency.lastUpdatedAtMs, true)}
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          </>
        ) : (
          <div className='performanceEmptyPanel'>
            <p>The competency ledger is waiting for more reviewed assignments, course progress, or Firefly evidence before it can build course-specific skills signals.</p>
          </div>
        )}
      </div>

      <div className='performancePanel performancePanelFull'>
        <div className='performancePanelHeader'>
          <h5>Cross-Platform Learning Signals</h5>
          <span className='performanceSub'>Assignment intelligence and Firefly tutoring</span>
        </div>

        <div className='performanceGrid performanceGridSignals'>
          <div className='performanceSignalPanel'>
            <span className='performanceSignalLabel'>Assignment learning profile</span>
            {assignmentProfileSummary?.headline ? (
              <>
                <strong>{assignmentProfileSummary.headline}</strong>
                <p>{assignmentProfileSummary.learnerSnapshot || 'Your reviewed assignments are building a clearer learning pattern.'}</p>
                <div className='performanceChipRow'>
                  {assignmentProfileSummary.readinessBand ? <span className='performanceChip'>{assignmentProfileSummary.readinessBand}</span> : null}
                  {assignmentProfileSummary.evidenceStrength ? <span className='performanceChip performanceChipAlt'>{assignmentProfileSummary.evidenceStrength} evidence</span> : null}
                  {assignmentProfileSummary.reviewCount ? <span className='performanceChip performanceChipMuted'>{assignmentProfileSummary.reviewCount} reviews</span> : null}
                </div>
              </>
            ) : (
              <p>No generated assignment learning profile is available yet.</p>
            )}
          </div>

          <div className='performanceSignalPanel'>
            <span className='performanceSignalLabel'>Firefly learning profile</span>
            {fireflySummary ? (
              <>
                <strong>{fireflySummary.headline || 'Firefly tutoring profile'}</strong>
                <p>{fireflySummary.studentSummary || 'Firefly session data will keep sharpening your tutoring profile over time.'}</p>
                <div className='performanceChipRow'>
                  <span className='performanceChip'>{fireflySummary.sessionCount} sessions</span>
                  {fireflySummary.readinessBand ? <span className='performanceChip performanceChipAlt'>{fireflySummary.readinessBand}</span> : null}
                  {fireflySummary.evidenceStrength ? <span className='performanceChip performanceChipWarn'>{fireflySummary.evidenceStrength} evidence</span> : null}
                </div>
                {fireflySummary.focusConcepts.length > 0 ? (
                  <div className='performanceChipRow'>
                    {fireflySummary.focusConcepts.map((item) => (
                      <span className='performanceChip performanceChipMuted' key={`concept-${item}`}>{item}</span>
                    ))}
                  </div>
                ) : null}
                <Link to='/firefly-reports' className='performanceInlineLink'>Open Firefly reports</Link>
              </>
            ) : (
              <p>No Firefly profile has been generated yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default StudentPerformanceComp
